import classNames from 'classnames';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useI18n } from 'common/hooks';
import svgIcons, { SvgProps } from 'common/svgIcons';

import { Dropdown, DropdownOption } from 'components/Dropdown/Dropdown.component';

import SvgIconUrl from '../SvgIconUrl/SvgIconUrl.component';
import './Button.css';

export interface ButtonProps
  extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  'data-test'?: string;
  loading?: boolean;
  loadingText?: string;
  outline?: boolean;
  small?: boolean;
  extraSmall?: boolean;
  medium?: boolean;
  large?: boolean;
  stretch?: boolean;
  action?: boolean;
  trade?: boolean;
  uppercase?: boolean;
  hidden?: boolean;
  fit?: boolean;
  inactive?: boolean;
  download?: boolean;
  deleted?: boolean;
  blocked?: boolean;
  circle?: boolean;
  color?:
    | 'green'
    | 'red'
    | 'orange'
    | 'white'
    | 'transparent'
    | 'transparent-green'
    | 'transparent-red'
    | 'transparent-purple'
    | 'black';
  position?: 'center';
  to?: string;
  icon?: SvgProps;
  options?: Array<DropdownOption>;
  text?: boolean;
}

const Button: React.FC<React.PropsWithChildren<ButtonProps>> = React.memo(
  ({
    children,
    className = '',
    'data-test': dataAttr,
    loading,
    loadingText,
    disabled,
    small,
    extraSmall,
    stretch,
    medium,
    large,
    outline,
    action,
    trade,
    icon,
    uppercase,
    color,
    hidden,
    fit,
    to,
    deleted,
    blocked,
    circle,
    position,
    inactive,
    download,
    options,
    text,
    ...props
  }) => {
    const { translate } = useI18n();

    const buttonClassNames = classNames({
      button: true,
      // Button size
      'button--small': small,
      'button--extra-small': extraSmall,
      'button--medium': medium,
      'button--large': large,
      'button--full-width': stretch,
      'button--fit': fit,
      // Button style
      'button--outline': outline,
      'button--text': text,
      'button--action': action,
      'button--trade': trade,
      'button--uppercase': uppercase,
      'button--dropdown': options,
      // Button state
      'button--loading': loading,
      'button--hidden': hidden,
      'button--disabled': disabled,
      'button--blocked': blocked,
      'button--circle': circle,
      'button--inactive': inactive,
      'button--download': download,
      'button--delete': deleted,
      // Custom class name
      [`button--${position}`]: position,
      [`button--${color}`]: color,

      [className]: className,
    });

    const isStringChildren = useMemo(() => typeof children === 'string', [children]);

    if (to) {
      return (
        <Link to={to} data-test={dataAttr} type='button' className={buttonClassNames}>
          {loading && (
            <span className='button--status'>{loadingText || translate.toText('mobile:button:processing')}</span>
          )}
          {icon && <SvgIconUrl className='button__icon' symbol={icon} />}
          {children}
        </Link>
      );
    }

    const buttonDropdownClassNames = classNames({
      'button-dropdown': true,
    });

    if (options) {
      return (
        <Dropdown
          options={options}
          placement='bottom'
          className={buttonDropdownClassNames}
          minWidth={({ contentWidth }) => contentWidth}
        >
          {(isActive) => (
            <button data-test={dataAttr} type='button' data-active={isActive} className={buttonClassNames} {...props}>
              {loading && (
                <span className='button--status'>{loadingText || translate.toText('mobile:button:processing')}</span>
              )}
              {isStringChildren ? <span>{children}</span> : children}
              <SvgIconUrl className='button__icon' data-active={isActive} symbol={icon || svgIcons.ChevronDownThin()} />
            </button>
          )}
        </Dropdown>
      );
    }

    return (
      <button data-test={dataAttr} type='button' className={buttonClassNames} disabled={disabled} {...props}>
        {loading && (
          <span className='button--status'>{loadingText || translate.toText('mobile:button:processing')}</span>
        )}
        {icon && <SvgIconUrl className='button__icon' symbol={icon} />}
        {isStringChildren ? <span>{children}</span> : children}
      </button>
    );
  },
);

export default Button;
